import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';

const DynamicProductsSection = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const q = query(
                    collection(db, 'products'),
                    where('status', '==', 'published'),
                    orderBy('sortOrder', 'asc')
                );
                const snapshot = await getDocs(q);
                const productsData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setProducts(productsData);
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    if (loading) {
        return null;
    }

    if (products.length === 0) {
        return null;
    }

    // Calculate if products need scrolling
    // Each card is 320px (w-80) + 32px margin (mx-4) = 352px total
    // Viewport is roughly 1280px (max-w-screen-xl) on desktop
    // If products fit within viewport, no need to scroll
    const cardWidth = 352; // 320px + margins
    const viewportWidth = 1280; // Approximate desktop viewport
    const shouldScroll = products.length * cardWidth > viewportWidth;

    // Duplicate products array for seamless infinite scroll only if needed
    const displayProducts = shouldScroll ? [...products, ...products, ...products] : products;

    return (
        <section className="py-20 bg-gradient-to-br from-[#FFF5F2] to-[#FFE8E0] overflow-hidden">
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
                <div className="text-center">
                    <p className="text-sm uppercase tracking-[0.3em] text-[#E85D44] mb-3">
                        OUR COLLECTION
                    </p>
                    <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
                        Personalized Products
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Transform your memories into unique, handcrafted treasures
                    </p>
                </div>
            </div>

            {/* Carousel or Grid */}
            <div className="relative">
                <div className={`flex ${shouldScroll ? 'animate-scroll-products hover:pause-animation' : 'justify-center flex-wrap'}`}>
                    {displayProducts.map((product, index) => (
                        <Link
                            key={`${product.id}-${index}`}
                            to={`/products/${product.slug}`}
                            className="flex-shrink-0 w-80 mx-4 group mb-8"
                        >
                            <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2">
                                {/* Product Image */}
                                <div className="aspect-[4/5] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative">
                                    {product.primaryImage ? (
                                        <img
                                            src={product.primaryImage.url}
                                            alt={product.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                                            No Image
                                        </div>
                                    )}
                                    {product.badge && (
                                        <span className="absolute top-4 left-4 bg-[#E85D44] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                                            {product.badge}
                                        </span>
                                    )}
                                    {/* Hover Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                                        <span className="text-white font-semibold text-sm uppercase tracking-wider">
                                            View Details →
                                        </span>
                                    </div>
                                </div>

                                {/* Product Info */}
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#E85D44] transition-colors line-clamp-1">
                                        {product.name}
                                    </h3>
                                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                        {product.shortDescription}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-2xl font-bold text-gray-900">
                                            {(() => {
                                                const enabledSizes = (product.sizes || []).filter(s => s.enabled);
                                                const currency = product.priceCurrency || 'Rs.';

                                                if (enabledSizes.length > 0) {
                                                    const prices = enabledSizes.map(s => s.priceDelta || product.price);
                                                    const minPrice = Math.min(...prices);
                                                    const maxPrice = Math.max(...prices);

                                                    if (minPrice !== maxPrice) {
                                                        return `${currency} ${minPrice.toLocaleString()} - ${maxPrice.toLocaleString()}`;
                                                    }
                                                    return `${currency} ${minPrice.toLocaleString()}`;
                                                }
                                                return `${currency} ${product.price?.toLocaleString()}`;
                                            })()}
                                        </span>
                                        <div className="w-10 h-10 rounded-full bg-[#E85D44]/10 flex items-center justify-center group-hover:bg-[#E85D44] transition-colors">
                                            <svg className="w-5 h-5 text-[#E85D44] group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Gradient Overlays - only show if scrolling */}
                {shouldScroll && (
                    <>
                        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#FFF5F2] to-transparent pointer-events-none z-10"></div>
                        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#FFE8E0] to-transparent pointer-events-none z-10"></div>
                    </>
                )}
            </div>

            {/* View All CTA */}
            <div className="text-center mt-12">
                <Link
                    to="/products"
                    className="inline-block px-8 py-3 bg-[#E85D44] text-white font-semibold text-sm uppercase tracking-wider rounded-lg hover:bg-[#d44d34] transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                    View All Products
                </Link>
            </div>

            {shouldScroll && (
                <style jsx>{`
                    @keyframes scroll-products {
                        0% {
                            transform: translateX(0);
                        }
                        100% {
                            transform: translateX(calc(-352px * ${products.length}));
                        }
                    }

                    .animate-scroll-products {
                        animation: scroll-products ${products.length * 8}s linear infinite;
                    }

                    .pause-animation:hover {
                        animation-play-state: paused;
                    }
                `}</style>
            )}
        </section>
    );
};

export default DynamicProductsSection;
